import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, FuntourMark, PasswordField, TextField } from '@rooming/ui';
import { isProblem, useCountdown } from '@rooming/web-core';
import { useState, type SyntheticEvent } from 'react';
import { passengerApi } from '../api';
import { messageOf } from '../messages';

/**
 * Passenger and guardian login (DR-41, Q-AUTH-7). The first password is the document number; the server then allows
 * only the password change. The error never says which of username/password was wrong (anti-enumeration).
 */
export function LoginPage() {
  const queryClient = useQueryClient();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  // 429 with Retry-After: the shared countdown keeps the button disabled (web-core).
  const [waitSeconds, setWaitSeconds] = useCountdown();

  async function submit(event: SyntheticEvent): Promise<void> {
    event.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      const { data } = await passengerApi.login(username.trim(), password);
      setPassword('');
      queryClient.setQueryData(
        ['session'],
        data.nextStep === 'CREDENTIAL_CHANGE' ? 'CREDENTIAL_CHANGE_PENDING' : 'ACTIVE',
      );
    } catch (problem) {
      setError(messageOf(problem));
      if (isProblem(problem) && problem.retryAfterSeconds !== undefined) setWaitSeconds(problem.retryAfterSeconds);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-md py-2 sm:py-8">
      <div className="rounded-3xl border border-line bg-surface p-6 shadow-xl sm:p-8">
        <div className="mb-6 flex items-center gap-3">
          <FuntourMark size={44} />
          <b className="text-lg">Funtour</b>
        </div>
        <p className="text-sm font-bold tracking-widest text-brand-700 uppercase">Bienvenido</p>
        <h1 className="mt-2 text-3xl font-bold">Ingresar</h1>
        <p className="mt-2 mb-6 text-base text-muted">
          Ingresá con el usuario que te dio tu coordinador. Si no lo recordás, está en el correo de bienvenida o te lo
          da tu coordinador.
        </p>
        <form className="flex flex-col gap-4" onSubmit={(event) => void submit(event)} noValidate>
          <TextField
            label="Usuario o Documento"
            name="username"
            placeholder="jperez o número de documento"
            inputMode="text"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            required
            value={username}
            onChange={(event) => {
              setUsername(event.target.value);
            }}
          />
          <PasswordField
            label="Contraseña"
            name="password"
            autoComplete="current-password"
            hint="La primera vez es tu número de documento, sin puntos ni guion."
            required
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />
          {error !== undefined && (
            <Alert tone="error">
              {error}
              {waitSeconds > 0 && ` (${String(waitSeconds)} s)`}
            </Alert>
          )}
          <Button
            type="submit"
            fullWidth
            loading={busy}
            disabled={waitSeconds > 0 || username.trim() === '' || password === ''}
          >
            Ingresar
          </Button>
        </form>
      </div>
    </div>
  );
}
